import React from "react";
import { Modal } from "react-bootstrap";
import { handleApiError } from "utils/errorHandler";
import { getAuthCookie } from "../../utils/authCookies";

type CouponExpirationCheckProps = {
  token: string;
  supplierId: string;
  customerId: string;
  discountCode: string;
  products: Array<{ product_id: string; variant_id: string }>;
  onValidCoupon: () => void;      // Continue with discount
  onExpiredAccepted: () => void;  // User clicked OK - remove discount and continue
  onExpiredCancelled: () => void; // User clicked Cancel - stay on page
};

type CheckResult = {
  isValid: boolean;
  couponCode?: string;
  error?: string;
};

export default function CouponExpirationCheck({
  token,
  supplierId,
  customerId,
  discountCode,
  products,
  onValidCoupon,
  onExpiredAccepted,
  onExpiredCancelled,
}: CouponExpirationCheckProps) {
  const [showModal, setShowModal] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [couponName, setCouponName] = React.useState("");

  /**
   * Validate coupon with backend
   * Returns { isValid: true } if coupon is still valid
   * Returns { isValid: false, couponCode } if expired
   */
  const validateCoupon = async (): Promise<CheckResult> => {
    if (!discountCode || !discountCode.trim()) {
      // No coupon applied, proceed normally
      return { isValid: true };
    }

    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${process.env.REACT_APP_API_URL}/discounts/signages/${encodeURIComponent(
      supplierId
    )}/discounts/resolve`;
    const requestBody = {
      discount_code: discountCode.trim(),
      customer_id: customerId,
      products: products.filter(p => p.product_id && p.variant_id),
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });

      // If response is OK, coupon is still valid
      if (response.ok) {
        return { isValid: true };
      }

      // If response is not OK, coupon is expired or invalid
      return { isValid: false, couponCode: discountCode };
    } catch (error) {
      console.error("Error validating coupon:", error);
      await handleApiError(error, {
        module: "other",
        action: "CouponExpirationCheck_validate_coupon",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url,
        method: "POST",
        requestBody,
        // headers,
        additionalInfo: {
          supplierId,
          customerId,
          discountCode: discountCode.trim(),
        },
      }, token);
      // On network error, treat as invalid and show popup
      return { isValid: false, couponCode: discountCode, error: "Unable to validate coupon" };
    }
  };

  /**
   * Exposed method to trigger the check
   * Call this before proceeding to checkout
   */
  const checkAndProceed = async () => {
    setChecking(true);
    
    const result = await validateCoupon();
    
    setChecking(false);

    if (result.isValid) {
      // Coupon is valid, proceed normally
      onValidCoupon();
    } else {
      // Coupon is expired, show popup
      setCouponName(result.couponCode || discountCode);
      setShowModal(true);
    }
  };

  const handleOk = () => {
    setShowModal(false);
    onExpiredAccepted(); // Remove coupon and continue
  };

  const handleCancel = () => {
    setShowModal(false);
    onExpiredCancelled(); // Stay on page
  };

  return {
    checkAndProceed,
    checking,
    modal: (
      <Modal
        show={showModal}
        onHide={handleCancel}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="border-bottom-0 pb-0">
          <Modal.Title className="d-flex align-items-center gap-2">
            <i className="ri-error-warning-line fs-3 text-warning"></i>
            <span>Coupon Expired</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="mb-3">
            Your applied coupon <strong>"{couponName}"</strong> has expired.
          </p>
          <p className="mb-0">
            Are you sure you want to continue without the discount?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOk}
          >
            OK, Continue Without Discount
          </button>
        </Modal.Footer>
      </Modal>
    ),
  };
}

// =====================================
// Hook version for easier usage
// =====================================
export function useCouponExpirationCheck({
  token,
  supplierId,
  customerId,
  discountCode,
  products,
  onRemoveCoupon,
}: {
  token: string;
  supplierId: string;
  customerId: string;
  discountCode: string;
  products: Array<{ product_id: string; variant_id: string }>;
  onRemoveCoupon: () => void;
}) {
  const [showModal, setShowModal] = React.useState(false);
  const [couponName, setCouponName] = React.useState("");
  const [checking, setChecking] = React.useState(false);
  const [couponRemoved, setCouponRemoved] = React.useState(false);
  const proceedCallbackRef = React.useRef<(() => void) | null>(null);

  /**
   * Check coupon and either proceed or show modal
   */
  const checkAndProceed = React.useCallback(
    async (onProceed: () => void) => {
      // Reset removal flag
      setCouponRemoved(false);
      
      // Store the proceed callback
      proceedCallbackRef.current = onProceed;

      // If no coupon applied, proceed immediately
      if (!discountCode || !discountCode.trim()) {
        onProceed();
        return;
      }

      const userId = getAuthCookie("userId");
      const licenseType = getAuthCookie("licenseType");
      const userName = getAuthCookie("userName");
      const url = `${process.env.REACT_APP_API_URL}/discounts/signages/${encodeURIComponent(
        supplierId
      )}/discounts/resolve`;
      const requestBody = {
        discount_code: discountCode.trim(),
        customer_id: customerId,
        products: products.filter((p) => p.product_id && p.variant_id),
      };
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      setChecking(true);

      try {
        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          // ✅ Coupon is still valid, proceed
          onProceed();
        } else {
          // ❌ Coupon expired or invalid, show modal
          setCouponName(discountCode);
          setShowModal(true);
        }
      } catch (error) {
        console.error("Error validating coupon:", error);
        await handleApiError(error, {
          module: "other",
          action: "CouponExpirationCheck_check_and_proceed",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url,
          method: "POST",
          requestBody,
          // headers,
          additionalInfo: {
            supplierId,
            customerId,
            discountCode: discountCode.trim(),
          },
        }, token);
        // On network error, show modal
        setCouponName(discountCode);
        setShowModal(true);
      } finally {
        setChecking(false);
      }
    },
    [token, supplierId, customerId, discountCode, products]
  );

  const handleOk = () => {
    setShowModal(false);
    setCouponRemoved(true);
    
    // console.log("[COUPON_EXPIRATION] User clicked OK - removing coupon:", couponName);
    
    // ✅ Remove coupon from parent state
    onRemoveCoupon();
    
    // ✅ Wait for React state updates to complete, then proceed
    // Increased timeout to ensure all dependent state (uiDiscount, perItemCouponMap, etc.) recalculates
    setTimeout(() => {
      // console.log("[COUPON_EXPIRATION] Proceeding after coupon removal");
      if (proceedCallbackRef.current) {
        proceedCallbackRef.current();
      }
    }, 500); // Increased timeout to ensure all state updates propagate
  };

  const handleCancel = () => {
    setShowModal(false);
    setCouponRemoved(false);
    // Do nothing - stay on current page
  };

  const Modal_Component = (
    <Modal
      show={showModal}
      onHide={handleCancel}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="ri-error-warning-line fs-3 text-warning"></i>
          <span>Coupon Expired</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <p className="mb-3">
          Your applied coupon <strong className="text-danger">"{couponName}"</strong> has expired.
        </p>
        <p className="mb-3 fw-semibold">
          If you continue, the discount will be removed and your order will be placed at the original price.
        </p>
        <p className="mb-0">
          Are you sure you want to continue without the discount?
        </p>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleOk}
        >
          OK, Continue Without Discount
        </button>
      </Modal.Footer>
    </Modal>
  );

  return {
    checkAndProceed,
    checking,
    Modal: Modal_Component,
  };
}

